import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import confetti from 'canvas-confetti';

const SLIDES = [
  {
    src: 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/ChatGPT%20Image%20May%209%2C%202026%2C%2012_16_41%20PM-JgdtM83fRhXyqh5EICA6rjDcPvdI3b.png',
    alt: 'ICRS 30th Anniversary limited-edition navy hat — front view',
  },
  {
    src: 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/ChatGPT%20Image%20May%208%2C%202026%2C%2004_38_24%20PM%20%281%29-JlmWTeIofei3Ap26aklgvHg61IBECY.png',
    alt: 'ICRS 30th Anniversary limited-edition navy hat — all angles',
  },
  {
    src: 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/ChatGPT%20Image%20May%209%2C%202026%2C%2011_39_21%20AM-L2fgv2qIDtcyMRRYhPDOoo21TAG11p.png',
    alt: 'Exhibition hall floor plan showing Rentid booth location',
  },
];

const HeroSection = () => {
  const scrollRef = useRef(null);
  const [active, setActive] = useState(0);

  // Fire a celebratory confetti burst from both sides once on mount.
  useEffect(() => {
    // Color buckets: 50% teal, 20% purple, 10% blue, 20% mixed.
    const COLORS = [
      ...Array(50).fill('#33F7BE'),
      ...Array(20).fill('#9747FF'),
      ...Array(10).fill('#1DC3EC'),
      '#e63946', '#f1faee', '#a8dadc', '#457b9d', '#1d3557',
      '#ffbe0b', '#fb5607', '#ff006e', '#8338ec', '#3a86ff',
    ];

    const fire = (particleRatio, opts) => {
      confetti({
        ...opts,
        particleCount: Math.floor(400 * particleRatio),
        spread: 60,
        startVelocity: 65,
        ticks: 220,
        zIndex: 9999,
        colors: COLORS,
      });
    };

    // Left cannon
    fire(0.25, { angle: 60, origin: { x: 0, y: 0.65 } });
    fire(0.2, { angle: 75, origin: { x: 0, y: 0.65 } });

    // Right cannon
    fire(0.25, { angle: 120, origin: { x: 1, y: 0.65 } });
    fire(0.2, { angle: 105, origin: { x: 1, y: 0.65 } });

    // Second burst
    const timeout = setTimeout(() => {
      fire(0.1, { angle: 60, origin: { x: 0, y: 0.65 } });
      fire(0.1, { angle: 120, origin: { x: 1, y: 0.65 } });
    }, 400);

    return () => clearTimeout(timeout);
  }, []);

  const scrollTo = (index) => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTo({ left: container.offsetWidth * index, behavior: 'smooth' });
    setActive(index);
  };

  const prev = () => scrollTo(Math.max(active - 1, 0));
  const next = () => scrollTo(Math.min(active + 1, SLIDES.length - 1));

  return (
    <section className="icrs-hero">
      <div className="icrs-hero-body">
        <div className="icrs-hero-grid">
          {/* Left — text */}
          <div className="icrs-hero-left">
            <div className="icrs-pill">
              <span className="icrs-pill__dot">
                <span className="icrs-pill__dot-ping" />
                <span className="icrs-pill__dot-core" />
              </span>
              <span className="icrs-pill__text">You've claimed your ICRS Gift</span>
            </div>

            <p className="icrs-eyebrow">Limited Edition Giveaway</p>
            <h1 className="icrs-hero-title">
              The ICRS 30th<br />Anniversary Hat
            </h1>

            <p className="icrs-hero-lead">
              Your exclusive ICRS 30th Anniversary hat is ready &mdash; stop by{' '}
              <button type="button" onClick={() => scrollTo(2)} className="icrs-inline-btn">
                Booth 400
              </button>{' '}
              to pick it up and see how Rentid is innovating and transforming the rental
              industry for your business.
            </p>

            <p className="icrs-hero-disclaimer">
              1 per person &nbsp;&middot;&nbsp; while supplies last
            </p>

            <div className="icrs-booth-note">
              <span className="icrs-booth-note__emoji" role="img" aria-label="waving hand">👋</span>
              <p className="icrs-booth-note__text">
                Show this screen to a Rentid or ICRS staff member at Booth 400 to claim
                your hat.
              </p>
            </div>
          </div>

          {/* Right — image carousel */}
          <div className="icrs-hero-right">
            <div className="icrs-carousel-wrap">
              <div className="icrs-carousel-badge">
                <img
                  src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/ChatGPT%20Image%20May%208%2C%202026%2C%2004_38_25%20PM%20%282%29-4vua68PTC97QkIyKsn3vRU1mNzlptZ.png"
                  alt="ICRS 30th Anniversary commemorative coin"
                />
              </div>

              <div className="icrs-carousel-track" ref={scrollRef}>
                {SLIDES.map((slide, i) => (
                  <div key={i} className="icrs-carousel-slide">
                    <img src={slide.src} alt={slide.alt} />
                  </div>
                ))}
              </div>
            </div>

            {/* Controls: arrows + dots */}
            <div className="icrs-carousel-controls">
              <button
                type="button"
                onClick={prev}
                disabled={active === 0}
                aria-label="Previous slide"
                className="icrs-carousel-arrow"
              >
                <ChevronLeft size={16} style={{ color: '#171717' }} />
              </button>

              <div className="icrs-carousel-dots">
                {SLIDES.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => scrollTo(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`icrs-carousel-dot${i === active ? ' is-active' : ''}`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={next}
                disabled={active === SLIDES.length - 1}
                aria-label="Next slide"
                className="icrs-carousel-arrow"
              >
                <ChevronRight size={16} style={{ color: '#171717' }} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
